'use client';
import { useEffect, useState } from 'react';
import { Banner, ComboBox } from '@airbus/components-react';
import Spinners from '../../shared/components/Spinner';
import Layout from '../../shared/components/layout';
import useFetchScreenDetails from '../../shared/utils/useFetchScreenDetails';

import GetAllColumnContextProvider, {
  useColumnContextAPI,
} from '../../shared/context/GetAllColumnContext';
import { getNpiHtzDetails } from '../../features/po-monitor/services/getNpiHtzDetails';
import ColumnConfig from '../../shared/components/grid/SetColumnConfig';
import { ColloborationDropdownOptionsGroup } from '../../features/po-monitor/components/CollaborationStateDecision';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { unblockPO } from '../../features/po-monitor/services/unblockPoService';
import style from '../../shared/components/grid/RenderEditField.module.css';
import { t } from '../../i18next';
import { focusField } from '../../shared/components/grid/RenderEditField';
import GridWrapper from '../../shared/components/grid/GridWrapper';
import { postRequest } from '../../shared/utils';
import { useAlertBar } from '../../shared/context/AlertBarContext';
import { useGridDataContext } from '../../shared/context/GridDataContext';

const POMonitorPage = () => {
  const { loading, error, screenTables } = useFetchScreenDetails('po_monitor');

  return (
    <>
      {loading ? (
        <Spinners />
      ) : (
        <Layout>
          {error && <Banner variant='warning'>{t('data_not_available')}</Banner>}
          {!screenTables
            ? null
            : screenTables.map((item) => (
                <GetAllColumnContextProvider key={item.id}>
                  <PoMonitor tableRes={item} />
                </GetAllColumnContextProvider>
              ))}
        </Layout>
      )}
    </>
  );
};

export default POMonitorPage;

const PoMonitor = ({ tableRes }) => {
  const [loadPo, setLoadPo] = useState(false);
  const { setAlertbar } = useAlertBar();
  const { updateCustomConfig } = useColumnContextAPI();

  useEffect(() => {
    async function init() {
      try {
        setLoadPo(true);
        await unblockPO(tableRes?.id);
        setLoadPo(false);
        updateCustomConfig({
          CommitHandler,
          SetFormValue,
          SetColumnConfig,
          AdditionalFilterQuery: 'hidefromkubumon:{_eq:0}',
          AdditionalReportFilterModel: {
            items: [
              {
                field: 'hidefromkubumon',
                operator: '=',
                value: 0,
              },
            ],
            logicOperator: 'and',
          },
        });
      } catch {
        setAlertbar({ children: t('exception_occurred'), severity: 'error' });
      }
    }
    init();
  }, []);

  return loadPo ? (
    <Spinners />
  ) : (
    <>
      <GridWrapper tableRes={tableRes} />
    </>
  );
};
const SetFormValue = async (props) => {
  const {
    filteredColumns,
    rowValues,
    setInitialValues,
    setPanelLoader,
    setAlertbar,
    tableDetails,
  } = props;
  const returnColumns = filteredColumns.map((item) => item.attrInternalName);
  let result = null;
  try {
    const resp = await getNpiHtzDetails(rowValues.htz, tableDetails.id, returnColumns);
    result = resp?.npi_v_pomon_htz_details.length ? resp.npi_v_pomon_htz_details[0] : null;
  } catch (error) {
    setAlertbar({ children: t('error.exception', { msg: error.message }), severity: 'error' });
  }
  setInitialValues(result);
  setPanelLoader(false);
};

const CommitHandler = async (props) => {
  const { editedRow, allAccessibleColumns, tableDetails, userDetails } = props;

  const collobrationColumn = allAccessibleColumns.find(
    (item) => item.attrInternalName === 'po_kubu_id',
  );
  if (!collobrationColumn?.mutationTablePkey) {
    return;
  }
  const [viewId, muttationId] = collobrationColumn.mutationTablePkey.split(':');

  const updateObjArr = editedRow.map((item) => {
    const where = {};
    where[muttationId] = {
      _eq: item[viewId],
    };
    return {
      where,
      _set: {
        po_kubu_id: item.po_kubu_id,
        updated_by: userDetails.userId,
        update_date: new Date().toISOString().substring(0, 19),
      },
    };
  });

  try {
    const resp = await postRequest('/api/tables', {
      screenGridId: tableDetails.id,
      tables: [
        {
          schema: tableDetails.mbdDataGrid.schemaName,
          table: collobrationColumn.mutationTableName,
          pkey: muttationId,
          deleteIds: [],
          insertObjects: [],
          updateObjects: updateObjArr,
        },
      ],
    });
    if (resp?.errors) {
      return {
        errors: { message: t('error.action_failed_following', { msg: resp.errors[0].message }) },
      };
    }
    const unblockResp = await unblockPO(tableDetails.id);
    const { result_text: resultText } = unblockResp.npi_unblock_po_trigger_function;
    if (resultText !== 'Success') {
      return { errors: { message: t('unblock_trigger_failed') } };
    }
    return { success: true };
  } catch {
    return { errors: { message: t('exception_occurred') } };
  }
};

const SetColumnConfig = (props) => {
  const { gridColumns: columnsConfig } = ColumnConfig(props);
  const gridColumns = columnsConfig.reduce((resp, item) => {
    if (item.attrInternalName === 'po_kubu_id') {
      const customColumn = {
        ...item,
        renderEditCell: (params) => <FieldSingleSelect {...{ ...params, ...props.customProps }} />,
      };

      resp.push(customColumn);
      return resp;
    }
    resp.push(item);
    return resp;
  }, []);
  return { gridColumns };
};
const FieldSingleSelect = (prop) => {
  const { colDef, id, field, row } = prop;
  const [, setRows] = useGridDataContext();
  let { value } = prop;
  const apiRef = useGridApiContext();
  const { valueOptions } = colDef;
  const customOptions =
    ColloborationDropdownOptionsGroup?.[row?.po_action]?.[row?.backup?.po_kubu_id] ?? valueOptions;

  useEffect(() => {
    focusField({ id, field });
  }, []);

  const blurHandler = () => {
    apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
  };

  const onValueChangeHandler = (val, id) => {
    if (val) {
      value = val;
      apiRef.current.setEditCellValue({ id, field, value: val });
      setRows((allrows) => {
        const newState = [...allrows];
        const index = newState.findIndex((item) => item.id == id);
        if (index !== -1) {
          if (!newState[index].status) {
            newState[index] = {
              ...newState[index],
              po_kubu_id: val,
              status: 'E',
            };
          } else {
            newState[index] = {
              ...newState[index],
            };
          }
        }
        return newState;
      });
    }
  };

  return (
    <div className={style.render_edit_cell}>
      <ComboBox
        className='h-100'
        id={id + '_' + field}
        hideClear={true}
        options={customOptions}
        value={valueOptions.find((item) => item.value == value)}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        onChange={(obj) => onValueChangeHandler(obj == '' ? obj : obj.value, id)}
        onBlur={blurHandler}
      ></ComboBox>
    </div>
  );
};
